// httpPorts — the Generation slice (phase 2c side view). Commands go over the
// command bus; the live feed is the job's SSE stream. Every payload is parsed
// at this boundary (generationAdapter.ts).

import { Result } from "@bilda/utils";
import {
    parseGenerationJob,
    parseGenerationSkeleton,
    parseGenerationSnapshot,
} from "../generationAdapter.js";
import { openGenerationStream } from "../httpClient.js";
import type {
    GenerationJobSnapshot,
    GenerationPorts,
} from "../ports/generation.js";
import type { PortResult } from "../ports/result.js";
import { command } from "./shared.js";

const snapshotCommand = async (
    name: string,
    body: unknown,
): PortResult<GenerationJobSnapshot> => {
    const r = await command(name, body);
    return Result.isErr(r) ? r : parseGenerationSnapshot(r.value);
};

// A frame that does not parse is dropped (the next whole snapshot supersedes
// it); a frame that is not JSON at all likewise.
const parseFrame = (data: string): GenerationJobSnapshot | undefined => {
    let raw: unknown;
    try {
        raw = JSON.parse(data);
    } catch {
        return undefined;
    }
    const r = parseGenerationSnapshot(raw);
    return Result.isOk(r) ? r.value : undefined;
};

export const generationHttp: GenerationPorts = {
    loadGenerationSkeleton: async (root) => {
        const r = await command("showResource", {
            id: root,
            expectedKind: "material",
        });
        return Result.isErr(r) ? r : parseGenerationSkeleton(r.value);
    },
    showGenerationJob: async (root) => {
        const r = await command("showGenerationJob", { root });
        return Result.isErr(r) ? r : parseGenerationJob(r.value);
    },
    startGeneration: (root, parts) =>
        snapshotCommand(
            "startGeneration",
            parts === undefined ? { root } : { root, parts },
        ),
    stopGeneration: (root) => snapshotCommand("stopGeneration", { root }),
    continueGeneration: (root) =>
        snapshotCommand("continueGeneration", { root }),
    reviseGenerationPart: (root, part, feedback) =>
        snapshotCommand("reviseGenerationPart", { root, part, feedback }),
    markBuildsOnStillFine: async (part, dependsOn) =>
        Result.map(() => undefined)(
            await command("markBuildsOnStillFine", { part, dependsOn }),
        ),
    advanceReference: async (root, refId, toVersion) =>
        Result.map(() => undefined)(
            await command("advanceReference", { root, refId, toVersion }),
        ),
    declineNewerVersions: async (root, refId) =>
        Result.map(() => undefined)(
            await command("declineNewerVersions", { root, refId }),
        ),
    approvePart: async (part, version) =>
        Result.map(() => undefined)(
            await command("approvePart", { part, version }),
        ),
    revokePartApproval: async (part, version) =>
        Result.map(() => undefined)(
            await command("revokePartApproval", { part, version }),
        ),
    watchGenerationJob: (root, onEvent) => {
        const source = openGenerationStream(root);
        source.onopen = () => onEvent({ type: "generation_feed_opened" });
        source.onerror = () => onEvent({ type: "generation_feed_lost" });
        source.onmessage = (e: MessageEvent<string>) => {
            const snapshot = parseFrame(e.data);
            if (snapshot !== undefined)
                onEvent({ type: "generation_feed_snapshot", snapshot });
        };
        return () => source.close();
    },
};
